'use client';

import { useState, useRef, useEffect } from 'react';
import { Search } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { getSearchResult } from '@/api/stock';
import type { SearchResult } from './types';
import { useDebounce } from '@/hooks/useDebounce';
import EmailModal from '../modal/variants/EmailModal';
import { useTranslations } from '@/lib/locale-context';

const SearchMarketData = () => {
  const { t, locale } = useTranslations();
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [apiResults, setApiResults] = useState<SearchResult[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  // Fetch external data on search
  useEffect(() => {
    const fetchExternalResults = async () => {
      if (!debouncedSearchTerm) {
        setApiResults([]);
        setLoading(false);
        return;
      }
      setLoading(true);

      try {
        const result: SearchResult[] =
          await getSearchResult(debouncedSearchTerm);
        setApiResults(result || []);
        setShowSearchResults(true);
      } catch (err) {
        console.error('Search API error:', err);
        setApiResults([]);
      } finally {
        setLoading(false);
      }
    };

    fetchExternalResults();
  }, [debouncedSearchTerm]);

  // Close search on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowSearchResults(false);
      }
    };

    if (showSearchResults) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchResults]);

  const handleResultClick = (result: SearchResult) => {
    if (result.id) {
      router.push(`/${locale}/markets/${result.id.toLowerCase()}`);
      setShowSearchResults(false);
    }
  };

  const handleInputClick = () => {
    setShowSearchResults(true);
    if (wrapperRef.current) {
      const top =
        wrapperRef.current.getBoundingClientRect().top +
        window.pageYOffset -
        250;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <div className="flex w-full justify-center bg-neutral-25 sm:bg-white">
      <div className="relative w-full rounded-[12px] bg-neutral-25 p-6">
        <div className="px-0 pb-[21px] pt-0">
          <h2 className="text-lg font-medium text-neutral-700">
            {t('markets.searchFinancialData')}
          </h2>
          <p className="text-base text-neutral-500">
            {t('markets.searchDescription')}
          </p>
        </div>

        <div className="px-0 pb-0">
          <div className="relative" ref={wrapperRef}>
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-neutral-800" />
            <input
              id="search-market-data"
              type="text"
              placeholder={t('markets.searchPlaceholder')}
              className="ring-offset-background focus:ring-blue-500 flex h-10 w-full max-w-[519px] rounded-[12px] border border-[#E5E7EB] bg-white px-3 py-2 pl-10 text-sm shadow-[0_6px_9.1px_-2px_rgba(137,140,156,0.10)] placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={() => handleInputClick()}
              aria-label={t('markets.searchAriaLabel')}
              autoComplete="off"
            />

            <div
              className={`absolute left-0 top-full z-50 mt-2 w-full transform rounded-lg border border-gray-200 bg-white shadow-lg transition-all duration-300 md:w-[519px] ${
                showSearchResults
                  ? 'visible scale-100 opacity-100'
                  : 'invisible scale-95 opacity-0'
              }`}
            >
              {loading ? (
                <div className="p-4 text-center text-gray-500">
                  {t('markets.searching')}
                </div>
              ) : apiResults.length > 0 ? (
                <div className="flex max-h-[500px] flex-col">
                  <div className="flex-1 overflow-y-auto">
                    {apiResults.map((result, index) => (
                      <div
                        key={index}
                        className="flex cursor-pointer items-center justify-between px-3 py-1 hover:bg-gray-50"
                        onClick={() => handleResultClick(result)}
                      >
                        <div className="flex items-center">
                          <div className="flex flex-col font-medium text-gray-800">
                            <div className="flex items-baseline gap-1">
                              <span className="text-sm">{result.name}</span>
                              <span className="rounded-xl bg-brand-100 px-2 py-1 text-[10px] font-medium text-brand-600">
                                {result.type}
                              </span>
                            </div>
                            {result.exchange && (
                              <span className="text-sm font-normal text-gray-400">
                                {result.exchange}
                              </span>
                            )}
                            {result.valor && (
                              <span className="text-sm font-normal text-gray-400">
                                {result.valor}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end">
                          <span className="text-sm text-gray-500">
                            {result.isin}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-neutral-50">
                    <div className="mx-3 mb-3 rounded-[8px] bg-brand-100 p-4 text-center">
                      <h3 className="mb-2 text-base font-semibold text-brand-800">
                        {t('markets.createWatchlist')}
                      </h3>
                      <p
                        className="cursor-pointer text-sm text-brand-500"
                        onClick={() => setIsOpen(true)}
                      >
                        {t('markets.notifyWhenAvailable')}
                        <EmailModal
                          isOpen={isOpen}
                          onClose={() => setIsOpen(false)}
                          onOpen={setIsOpen}
                          mainHeadline={{
                            title: t('markets.watchlistModalTitle'),
                            variant: 'headingThree',
                          }}
                          className="!h-fit"
                        />
                      </p>
                    </div>
                  </div>
                </div>
              ) : debouncedSearchTerm ? (
                // Show "no results" only if there's an active search term
                <div className="p-4 text-center text-gray-500">
                  {t('markets.noResults')}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchMarketData;
